import { useSyncExternalStore } from 'react'

// The flag flips at most once, and React re-checks the snapshot when hydration commits.
const subscribeNever = () => () => {}
const getFalse = () => false
const getTrue = () => true

/**
 * True on the server and on the hydration render, false from the next one. Per-component, unlike useHydrated, so
 * it stays correct inside a Suspense boundary React hydrates late (../components/ui/tooltip-ssr-gate.md).
 *
 * ONLY ever withhold something the server did NOT render: a client-only artefact, or a value ahead of the HTML.
 * Never gate rendered CONTENT on it — the page loses the value the server had, and the persisted cache with it.
 * Markup only the client can fill is served EMPTY with its value in an attribute instead (`countUpFromZero`).
 */
export function useIsHydratingRender(): boolean {
  return useSyncExternalStore(subscribeNever, getFalse, getTrue)
}

/**
 * The pages of an infinite list this render may show. The server can only ever render the FIRST one — every
 * loader prefetches a single page — but the persisted cache restores every page the last session scrolled
 * through, and react-query keeps that data because the streamed SSR query reaches the client still PENDING
 * (`hydrate` only replaces data with data). Those extra rows would hydrate against the markup the server
 * never wrote; they arrive one render later instead.
 */
export function useHydrationSafePages<TPage>(pages: ReadonlyArray<TPage>): ReadonlyArray<TPage> {
  return useIsHydratingRender() ? pages.slice(0, 1) : pages
}
